import classNames from 'classnames'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutCheckbox } from 'components/design-system/BreakoutCheckbox'
import { BreakoutTextArea } from 'components/design-system/BreakoutTextArea'
import { BreakoutTextInput } from 'components/design-system/BreakoutTextInput'
import { BreakoutInfoTooltip } from 'components/design-system/BreakoutPrefabTooltips'
import { FormError } from 'components/design-system/form/FormError'
import { InlineDialog } from 'components/dialogs/Dialog'
import { PercentageIcon } from 'components/icons/Percentage'
import type { useMeetingResultsCubit } from 'hooks/cubits/meetingsResults'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import {
  projectedFinalScore,
  useScoreComponents,
  type ScoreComponent,
  type ScoreComponentKey,
} from './scoreComponents'
import { MAX_GRADE_APPEAL_JUSTIFICATION_LENGTH } from '@breakoutlearning/firebase-repository/constants'
import {
  invalidateGradeAppeal,
  submitGradeAppeal,
  type AppealComponents,
} from './useGradeAppeal'
import { recoverGradeAppealSubmission } from './gradeAppealSubmissionRecovery'

type ComponentFields = {
  selected: boolean
  // A string so the field can start empty. The student picks the score they
  // want rather than being handed one, so nothing can be submitted untouched.
  proposedScore: string
  justification: string
}

type FormValues = {
  // Never rendered. Anchors the "at least one criterion" error so it has a
  // stable path both react-hook-form and the server's 422 can address.
  selection: boolean
  components: Record<ScoreComponentKey, ComponentFields>
}

function defaultValues(components: ScoreComponent[]): FormValues {
  const byKey = {} as Record<ScoreComponentKey, ComponentFields>
  for (const component of components) {
    byKey[component.key] = {
      selected: false,
      proposedScore: '',
      justification: '',
    }
  }
  return { selection: false, components: byKey }
}

export const GradeAppealForm = observer(function GradeAppealForm({
  onClose,
  cubit,
  canSubmit,
}: {
  onClose: () => void
  cubit: ReturnType<typeof useMeetingResultsCubit>
  canSubmit: boolean
}) {
  const { tt } = useTranslationTyped()
  const headingId = useId()
  const components = useScoreComponents(cubit)

  if (!components) return null

  return (
    <GradeAppealFormContent
      onClose={onClose}
      cubit={cubit}
      canSubmit={canSubmit}
      components={components}
      headingId={headingId}
      tt={tt}
    />
  )
})

const GradeAppealFormContent = observer(function GradeAppealFormContent({
  onClose,
  cubit,
  canSubmit,
  components,
  headingId,
  tt,
}: {
  onClose: () => void
  cubit: ReturnType<typeof useMeetingResultsCubit>
  canSubmit: boolean
  components: ScoreComponent[]
  headingId: string
  tt: ReturnType<typeof useTranslationTyped>['tt']
}) {
  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: defaultValues(components) })

  const confirmHeadingId = useId()
  const headingRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  // Validated payload held between the form submit and the confirmation step.
  // Non-null means the confirm dialog is open; `submitting` covers the POST it
  // fires, which is separate from the form's own `isSubmitting`.
  const [pendingPayload, setPendingPayload] = useState<AppealComponents | null>(
    null
  )
  const [submitting, setSubmitting] = useState(false)

  const watched = useWatch({ control, name: 'components' })

  // A component the instructor weighted at zero cannot change the grade, so it
  // is shown but not appealable.
  const appealable = useMemo(
    () => new Set(components.filter((c) => c.weight > 0).map((c) => c.key)),
    [components]
  )

  const proposedByKey = useMemo(() => {
    const out: Partial<Record<ScoreComponentKey, number>> = {}
    for (const component of components) {
      if (!appealable.has(component.key)) continue
      const fields = watched?.[component.key]
      if (!fields?.selected) continue
      if (fields.proposedScore === '') continue
      const value = Number(fields.proposedScore)
      if (Number.isFinite(value)) {
        out[component.key] = Math.min(100, Math.max(0, Math.round(value)))
      }
    }
    return out
  }, [watched, components, appealable])

  const currentTotal = projectedFinalScore(components)
  const desiredTotal = projectedFinalScore(components, proposedByKey)

  // Surface a desired-score problem (missing / out of range / not higher than
  // current) at the bottom-left, next to the selection error, instead of under
  // the input — that keeps the row height from shifting. The red outline on the
  // offending input still marks which one.
  const proposedScoreError = components
    .map((component) => errors.components?.[component.key]?.proposedScore)
    .find((error) => Boolean(error))

  // Overview shown on the confirmation step: the components being appealed with
  // their current → proposed score, plus the resulting change in the total. Read
  // straight off the validated payload so it matches exactly what is sent.
  const confirmProposedByKey: Partial<Record<ScoreComponentKey, number>> = {}
  const confirmRows: Array<{
    key: ScoreComponentKey
    label: string
    current: number | null
    proposed: number
  }> = []
  if (pendingPayload) {
    for (const component of components) {
      const entry = pendingPayload[component.key]
      if (!entry) continue
      confirmProposedByKey[component.key] = entry.proposedScore
      confirmRows.push({
        key: component.key,
        label: component.label,
        current: component.score,
        proposed: entry.proposedScore,
      })
    }
  }
  const confirmTotal = projectedFinalScore(components, confirmProposedByKey)
  const totalDelta = confirmTotal - currentTotal

  const onSubmit = async (values: FormValues) => {
    if (!canSubmit) return
    clearErrors()

    const selected = components.filter(
      (c) => appealable.has(c.key) && values.components[c.key].selected
    )

    if (selected.length === 0) {
      setError('selection', {
        message: tt.meeting.results.appeal.select_at_least_one(),
      })
      return
    }

    let hasFieldError = false
    for (const component of selected) {
      const justification =
        values.components[component.key].justification.trim()
      if (justification.length === 0) {
        setError(`components.${component.key}.justification`, {
          message: tt.meeting.results.appeal.justification_required(),
        })
        hasFieldError = true
      } else if (justification.length > MAX_GRADE_APPEAL_JUSTIFICATION_LENGTH) {
        setError(`components.${component.key}.justification`, {
          message: tt.meeting.results.appeal.justification_too_long({
            maxLength: MAX_GRADE_APPEAL_JUSTIFICATION_LENGTH,
          }),
        })
        hasFieldError = true
      }
      // One message covers every desired-score problem: empty, out of 0–100,
      // or not strictly greater than the current score (a null/unscored
      // component has no lower bound).
      const raw = values.components[component.key].proposedScore
      const proposed = Number(raw)
      // TODO(dse): it'd be nice to re-use the same validation logic from the server
      const invalidScore =
        raw === '' ||
        !Number.isFinite(proposed) ||
        proposed < 0 ||
        proposed > 100 ||
        (component.score !== null && Math.round(proposed) <= component.score)
      if (invalidScore) {
        setError(`components.${component.key}.proposedScore`, {
          message: tt.meeting.results.appeal.score_invalid(),
        })
        hasFieldError = true
      }
    }
    if (hasFieldError) return

    const payload: AppealComponents = {}
    for (const component of selected) {
      payload[component.key] = {
        proposedScore: Math.round(
          Number(values.components[component.key].proposedScore)
        ),
        justification: values.components[component.key].justification.trim(),
      }
    }

    // The appeal is one-shot and irreversible, so confirm before sending rather
    // than posting straight from the form submit.
    cubit.logEvent('grade_appeal_form_interaction', {
      action: 'confirmation_opened',
    })
    setPendingPayload(payload)
  }

  const performSubmit = async () => {
    if (!pendingPayload || submitting || !canSubmit) return
    setSubmitting(true)

    const [, error] = await submitGradeAppeal(cubit, pendingPayload)

    if (error) {
      setSubmitting(false)
      setPendingPayload(null)
      const recovered = await recoverGradeAppealSubmission({
        error,
        refresh: () => invalidateGradeAppeal(cubit),
        notify: (reason) =>
          toast.error(
            reason === 'already_appealed'
              ? tt.meeting.results.appeal.already_appealed()
              : tt.meeting.results.appeal.window_closed()
          ),
        close: onClose,
      })
      if (recovered) return

      toast.error(tt.meeting.results.appeal.submit_failed())
      return
    }

    await invalidateGradeAppeal(cubit)
    toast.success(tt.meeting.results.appeal.submitted())
    setSubmitting(false)
    setPendingPayload(null)
    onClose()
  }

  return (
    <>
      <section aria-labelledby={headingId} data-testid="grade-appeal-form">
        <form onSubmit={handleSubmit(onSubmit)} className="flex grow flex-col">
          <h2
            id={headingId}
            ref={headingRef}
            tabIndex={-1}
            className="text-xl font-bold text-black"
          >
            {tt.meeting.results.appeal.your_appeal()}
          </h2>
          <p className="mt-1.5 text-[13.5px] leading-relaxed font-medium text-bi-charcoal">
            {tt.meeting.results.appeal.explainer_description()}
          </p>

          <div className="mt-5 overflow-hidden rounded-2xl border border-bi-outline-light">
            {components.map((component) => {
              const isAppealable = appealable.has(component.key)
              const isSelected =
                watched?.[component.key]?.selected === true && isAppealable
              const fieldErrors = errors.components?.[component.key]
              const proposedScoreInputId = `${headingId}-${component.key}-proposed-score`
              const displayedScore =
                component.weight === 0 || component.score === null
                  ? '—'
                  : `${component.score}%`

              return (
                <div
                  key={component.key}
                  data-testid={`grade-appeal-row-${component.key}`}
                  data-weight-zero={!isAppealable ? 'true' : undefined}
                  className={classNames(
                    'relative border-b border-bi-outline-light px-4 py-4 last:border-b-0 sm:px-5',
                    { 'bg-bi-warning-soft': isSelected }
                  )}
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <Controller
                      control={control}
                      name={`components.${component.key}.selected`}
                      render={({ field }) => (
                        <BreakoutCheckbox
                          testId={`grade-appeal-${component.key}`}
                          value={field.value}
                          onChange={(checked) => {
                            field.onChange(checked)
                            clearErrors('selection')
                            cubit.logEvent('grade_appeal_form_interaction', {
                              action: 'component_toggled',
                              component: component.key,
                              selected: checked,
                            })
                          }}
                          disabled={!isAppealable}
                          aria-label={tt.meeting.results.appeal.select_component(
                            {
                              component: component.label,
                            }
                          )}
                        />
                      )}
                    />
                    <span
                      className={classNames(
                        'text-[15px] font-bold text-black',
                        {
                          'text-bi-on-surface-disabled': !isAppealable,
                        }
                      )}
                    >
                      {component.label}
                    </span>
                    <BreakoutInfoTooltip content={component.tooltip} />
                    <span className="text-xs font-medium text-bi-charcoal">
                      {tt.meeting.results.appeal.weight_of_grade({
                        weight: component.weight,
                      })}
                    </span>
                    <span
                      className="ml-auto text-lg font-bold text-black tabular-nums"
                      data-testid={`grade-appeal-current-${component.key}`}
                    >
                      {displayedScore}
                    </span>
                  </div>
                  {!isAppealable && (
                    <p className="mt-1 text-xs text-bi-on-surface-disabled">
                      {tt.meeting.results.appeal.not_weighted()}
                    </p>
                  )}
                  {isSelected && (
                    <div className="mt-4 flex flex-col gap-y-3">
                      <Controller
                        control={control}
                        name={`components.${component.key}.justification`}
                        render={({ field }) => (
                          <BreakoutTextArea
                            testId={`grade-appeal-justification-${component.key}`}
                            placeholder={tt.meeting.results.appeal.explainer_justification_placeholder()}
                            maxLength={MAX_GRADE_APPEAL_JUSTIFICATION_LENGTH}
                            hideCharacterCount
                            aria-label={tt.meeting.results.appeal.justification_label(
                              {
                                component: component.label,
                              }
                            )}
                            showErrorOutline={!!fieldErrors?.justification}
                            aria-invalid={!!fieldErrors?.justification}
                            inputClassName="min-h-[88px] text-body-small"
                            {...field}
                            onFocus={() =>
                              cubit.logEvent('grade_appeal_form_interaction', {
                                action: 'justification_focused',
                                component: component.key,
                              })
                            }
                            onChange={(event) => {
                              field.onChange(event)
                              clearErrors(
                                `components.${component.key}.justification`
                              )
                            }}
                          />
                        )}
                      />
                      <div className="flex flex-wrap items-center gap-2">
                        <label
                          htmlFor={proposedScoreInputId}
                          className="text-xs font-semibold text-bi-charcoal"
                        >
                          {tt.meeting.results.appeal.proposed_component_score({
                            component: component.label,
                          })}
                        </label>
                        <Controller
                          control={control}
                          name={`components.${component.key}.proposedScore`}
                          render={({ field }) => (
                            <BreakoutTextInput
                              id={proposedScoreInputId}
                              testId={`grade-appeal-proposed-${component.key}`}
                              type="number"
                              min={0}
                              max={100}
                              showErrorOutline={!!fieldErrors?.proposedScore}
                              aria-invalid={!!fieldErrors?.proposedScore}
                              TrailingIcon={PercentageIcon}
                              iconSize={14}
                              className="w-[104px]"
                              inputClassName="px-2 text-center"
                              value={field.value}
                              onFocus={() =>
                                cubit.logEvent(
                                  'grade_appeal_form_interaction',
                                  {
                                    action: 'proposed_score_focused',
                                    component: component.key,
                                  }
                                )
                              }
                              onChange={(event) => {
                                clearErrors(
                                  `components.${component.key}.proposedScore`
                                )
                                const raw = event.target.value
                                if (raw === '') return field.onChange('')
                                const parsed = Number(raw)
                                if (!Number.isFinite(parsed)) return
                                if (parsed < 0 || parsed > 100) return
                                field.onChange(raw)
                              }}
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}
                  {!isSelected && (
                    <span
                      className="sr-only"
                      data-testid={`grade-appeal-desired-${component.key}`}
                    >
                      {displayedScore}
                    </span>
                  )}
                  {fieldErrors?.justification && (
                    <span
                      className="sr-only"
                      data-testid={`grade-appeal-justification-error-${component.key}`}
                    >
                      {fieldErrors.justification.message}
                    </span>
                  )}
                  {fieldErrors?.proposedScore && (
                    <span
                      className="sr-only"
                      data-testid={`grade-appeal-proposed-error-${component.key}`}
                    >
                      {fieldErrors.proposedScore.message}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
          <div className="mt-3 flex justify-end">
            <div className="flex items-center gap-4 rounded-xl border border-bi-outline-light bg-bi-surface px-4 py-3">
              <span className="text-xs font-semibold text-bi-charcoal">
                {tt.meeting.results.appeal.appealed_score()}
              </span>
              <span
                className="text-2xl font-bold text-black tabular-nums"
                data-testid="grade-appeal-projected-total"
                aria-live="polite"
              >
                {desiredTotal}%
              </span>
            </div>
          </div>

          {errors.selection && (
            <FormError
              error={errors.selection}
              testId="grade-appeal-selection-error"
            />
          )}

          {proposedScoreError && (
            <FormError
              error={proposedScoreError}
              testId="grade-appeal-proposed-error"
            />
          )}

          <div className="mt-auto flex flex-row justify-between pt-6">
            <BreakoutButton
              type="button"
              kind="secondary"
              onClick={() => {
                cubit.logEvent('grade_appeal_form_interaction', {
                  action: 'form_cancelled',
                })
                onClose()
              }}
              data-testid="grade-appeal-cancel"
            >
              {tt.common.cancel()}
            </BreakoutButton>
            <BreakoutButton
              type="submit"
              kind="accent"
              loading={isSubmitting}
              disabled={isSubmitting || !canSubmit}
              onClick={() =>
                cubit.logEvent('grade_appeal_form_interaction', {
                  action: 'form_submit_clicked',
                })
              }
              data-testid="grade-appeal-submit"
            >
              {tt.meeting.results.appeal.submit()}
            </BreakoutButton>
          </div>
        </form>
      </section>

      {pendingPayload && (
        <InlineDialog
          open
          size="sm"
          onDismiss={() => {
            if (!submitting) {
              cubit.logEvent('grade_appeal_form_interaction', {
                action: 'confirmation_cancelled',
              })
              setPendingPayload(null)
            }
          }}
          dismissOnEscapeKey
          aria-labelledby={confirmHeadingId}
          innerClassName="flex flex-col justify-center"
          testId="grade-appeal-confirm-dialog"
        >
          <h1
            id={confirmHeadingId}
            className="text-center text-headline-small text-on-surface"
          >
            {tt.meeting.results.appeal.confirm_title()}
          </h1>
          <p className="mt-3 text-center text-body-medium text-on-surface-var">
            {tt.meeting.results.appeal.confirm_description()}
          </p>

          <div
            className="mt-5 rounded-xl border border-outline-light bg-surface-dim p-4"
            data-testid="grade-appeal-confirm-summary"
          >
            <ul className="flex flex-col gap-y-1">
              {confirmRows.map((row) => (
                <li
                  key={row.key}
                  className="text-body-small text-on-surface"
                  data-testid={`grade-appeal-confirm-row-${row.key}`}
                >
                  {tt.meeting.results.appeal.resolved_row({
                    component: row.label,
                    currentScore:
                      row.current === null
                        ? tt.meeting.results.appeal.resolved_row_current_none()
                        : `${row.current}%`,
                    proposedScore: row.proposed,
                  })}
                </li>
              ))}
            </ul>
            <div className="mt-3 flex items-center justify-between border-t border-outline-light pt-3">
              <div className="text-label-medium text-on-surface">
                {tt.meeting.results.appeal.final_score()}
              </div>
              <div
                className="flex items-center gap-2 text-label-medium"
                data-testid="grade-appeal-confirm-total"
              >
                <div className="text-on-surface-var">{currentTotal}%</div>
                <div className="text-on-surface-var">→</div>
                <div className="font-bold text-on-surface">{confirmTotal}%</div>
                <div
                  className={classNames('font-bold', {
                    'text-core-success': totalDelta > 0,
                    'text-core-error': totalDelta < 0,
                    'text-on-surface-var': totalDelta === 0,
                  })}
                >
                  {tt.meeting.results.appeal.confirm_delta({
                    points: `${totalDelta > 0 ? '+' : ''}${totalDelta}`,
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-row justify-between gap-3">
            <BreakoutButton
              type="button"
              kind="secondary"
              onClick={() => {
                cubit.logEvent('grade_appeal_form_interaction', {
                  action: 'confirmation_cancelled',
                })
                setPendingPayload(null)
              }}
              disabled={submitting}
              data-testid="grade-appeal-confirm-cancel"
            >
              {tt.common.cancel()}
            </BreakoutButton>
            <BreakoutButton
              type="button"
              kind="accent"
              loading={submitting}
              disabled={submitting || !canSubmit}
              onClick={() => {
                cubit.logEvent('grade_appeal_form_interaction', {
                  action: 'confirmation_submit_clicked',
                })
                void performSubmit()
              }}
              data-testid="grade-appeal-confirm-submit"
            >
              {tt.meeting.results.appeal.confirm_submit()}
            </BreakoutButton>
          </div>
        </InlineDialog>
      )}
    </>
  )
})
